import {useCallback, useEffect, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import type {Annotation, AnnotationAnchor} from './annotationTypes';
import {
  clearAnnotations,
  findAnnotationAtPoint,
  getSelectionOffsets,
  hasUntypesetMath,
  isHighlightApiSupported,
  observeSubtreeChanges,
  paintAnnotations
} from './annotationUtils';

const EMPTY_ANNOTATIONS: Annotation[] = [];

/**
 * `containerRef` is a callback ref so the paint effect re-runs if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(questionId: string): {
  captureAnnotation: () => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const annotations = useAnnotationModeStore(
    (state) => state.annotationsByQuestionId[questionId] ?? EMPTY_ANNOTATIONS
  );
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const setHasAnnotatableContent = useAnnotationModeStore(
    (state) => state.setHasAnnotatableContent
  );

  const captureAnnotation = useCallback((): AnnotationAnchor | null => {
    if (!container) {
      return null;
    }
    const selection = window.getSelection();
    const offsets = getSelectionOffsets(container);
    if (!selection || !offsets) {
      return null;
    }
    // Capture the rect before the selection is cleared below.
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    const id = addAnnotation(questionId, offsets);
    // Clear the native selection so it is replaced by the painted annotation.
    selection.removeAllRanges();
    return {rect, id};
  }, [addAnnotation, container, questionId]);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null =>
      container ? findAnnotationAtPoint(container, x, y, annotations) : null,
    [container, annotations]
  );

  useEffect(() => {
    if (!container || annotations.length === 0 || !isHighlightApiSupported()) {
      return undefined;
    }
    const repaint = () => paintAnnotations(container, annotations);
    // Paint now unless MathJax will still rewrite the prompt — in that case the
    // observer paints once it settles, avoiding a flash against the stale DOM.
    if (!hasUntypesetMath(container)) {
      repaint();
    }
    // Highlight Ranges are live, so a painted annotation would slide as MathJax
    // rewrites the prompt. Clear on mutation, repaint once the DOM settles.
    const stopObserving = observeSubtreeChanges(container, {
      onMutate: clearAnnotations,
      onSettle: repaint
    });
    return () => {
      stopObserving();
      clearAnnotations();
    };
  }, [container, annotations]);

  useEffect(() => {
    setHasAnnotatableContent(Boolean(container?.textContent?.trim()));
    return () => setHasAnnotatableContent(false);
  }, [container, setHasAnnotatableContent]);

  return {captureAnnotation, getAnnotationAtPoint, containerRef: setContainer};
}
